import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ProductService } from "./product.service";

const initialState = {
  products: [],
  searchResults: [],
  product: null,
  productHistory: null,
  historyLoading: false,
  historyError: null,
  pagination: null,
  summary: null,
  success: false,
  isLoading: false,
  error: null,
  message: null,
};

export const addProduct = createAsyncThunk(
  "product/addProduct",
  async (data, { rejectWithValue, getState }) => {
    try {
      const _productService = new ProductService();
      const response = await _productService.addProduct(data);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add product"
      );
    }
  }
);

export const getProducts = createAsyncThunk(
  "product/getProducts",
  async (params = {}, { rejectWithValue }) => {
    try {
      const _productService = new ProductService();
      const response = await _productService.getProducts(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load products");
    }
  }
);

export const searchProducts = createAsyncThunk(
  "products/search",
  async (searchTerm, { rejectWithValue, getState }) => {
    try {
      const _productService = new ProductService();
      const response = await _productService.searchProducts(searchTerm);

      if (!response || !response.data) {
        return [];
      }

      const products = Array.isArray(response.data) ? response.data : [response.data];
      return products.filter(product =>
        product &&
        typeof product === 'object' &&
        product.productName
      );
    } catch (error) {
      console.error('Search error:', error);
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to search products"
      );
    }
  }
);

export const getProduct = createAsyncThunk(
  "product/getProduct",
  async (id, { rejectWithValue, getState }) => {
    try {
      const _productService = new ProductService();
      const response = await _productService.getProduct(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getProductHistory = createAsyncThunk(
  "product/getHistory",
  async ({ id, page = 1, limit = 20, type } = {}, { rejectWithValue }) => {
    try {
      const _productService = new ProductService();
      const params = { page, limit };
      if (type && type !== 'all') params.type = type;
      const response = await _productService.getProductHistory(id, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to get product history"
      );
    }
  }
);

export const returnProduct = createAsyncThunk(
  "product/return",
  async ({ id, quantity, reason }, { rejectWithValue, getState }) => {
    try {
      const _productService = new ProductService();
      const response = await _productService.returnProduct(id, { quantity, reason });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to process product return"
      );
    }
  }
);

export const updateProduct = createAsyncThunk(
  "product/updateProduct",
  async (data, { rejectWithValue, getState }) => {
    try {
      const _productService = new ProductService();
      const response = await _productService.updateProduct(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "product/deleteProduct",
  async (id, { rejectWithValue, getState }) => {
    try {
      const _productService = new ProductService();
      const response = await _productService.deleteProduct(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.error = null;
      state.success = false;
      state.message = "";
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.isLoading = false;

        const payload = action.payload?.data;
        if (payload) {
          state.products = payload.products;
          state.pagination = payload.pagination || null;
          state.summary = payload.summary || null;
        } else {
          state.products = [];
        }
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load products";
      })

      .addCase(getProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.product = action.payload;
      })
      .addCase(getProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })

      .addCase(addProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products.unshift(action.payload);
        state.success = true;
        state.message = "Product added successfully";
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(searchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.searchResults = []; // Clear previous results while loading
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload;
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.searchResults = [];
        state.error = action.payload || "Failed to search products";
      })

      .addCase(getProductHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(getProductHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.productHistory = action.payload;
        state.historyError = null;
      })
      .addCase(getProductHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.productHistory = null;
        state.historyError = action.payload;
      })

      .addCase(returnProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(returnProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // Update product in products list
        const index = state.products.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
      })
      .addCase(returnProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(updateProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.product = action.payload;
        state.message = action.payload.message;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })

      .addCase(deleteProduct.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message = action.payload.message;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      });
  },
});

export const { reset, clearSearchResults } = productSlice.actions;

export default productSlice.reducer;
